import { parseTimestamp } from '@/utils/dateDisplay';
import { Model } from './model';
import { type BlobModel, CaseModel, EntryModel, type FormFieldChanges, type StudyChanges } from '@/models';
import { generateHexIdentifier, getRandomAdjective, LocalStorageUtils } from '@/utils';
import { getCurrentTimezone, getTimezone, type Timezone } from '@/utils/timezone';
import type { DrillData, DrillLevel } from '@/components/forms/form_builder/FormFieldConfigHelper';
import type { CustomIcon } from '@/components/common/interfaces/CustomIcon';
import { isAssignEffectPresent } from '@/components/data/entry/effects';
import type { DashboardCohortFilter } from './surgeonDashboard';
import type { UnitConversion } from './unitConversion';
import type { MatrixRowRule, MatrixRuleViolation } from './matrixRules';
import { uuid } from 'vue-uuid';

export interface NetworkForm {
  uri: string;
  hexIdentifier: string;
  identifier: string;
  creation: string;
  revisedOn: string;
  numFields: number;
  /**
   * On `GET study/:id/forms`, whether the caller may enter data on the form, from their effective
   * add-entry access. Absent from the single-form read, which sends `hasEntryPermission` instead.
   */
  edit?: boolean;
  title: string;
  investigationIdentifier?: string;

  enforcements?: FormEnforcements;
  items?: FormField[];
  anonymousResponsesAllowed?: boolean;
  editExistingEntry?: boolean;
  reminders?: NetworkReminders;
  translations?: FormTranslation[];

  sharedForm?: boolean;

  entries?: EntryModel[];
  welcomeScreenTemplate?: string;
  archivedAt?: string;
  entrySaveButtonText?: string;
  showSaveEntryButtonAtBottom?: boolean;
  hasEntryPermission?: boolean;
  timeSpentData?: TimeSpentOnForm;
  multipleEntriesError?: string;
  cachePublicEntry?: boolean;

  // Message to display when the form has data entry closed (closeEntry
  // enforcement). The closeEntry flag itself travels in `enforcements`.
  closedMessage?: string;

  /** Only on `GET form/:id`, and only when the request names a case. */
  links?: unknown;
}

/** One form's entry counts, as `GET study/:id/forms/entry-counts` sends them. */
export interface FormEntryCounts {
  /** This study's entries on the form. */
  entryCount: number;
  /**
   * On a form this study owns, the entries studies reading it as a shared form hold on it. Always
   * `null` on a shared form (docs/forms/REVIEW.md F5, decision 73).
   */
  otherStudiesEntryCount: number | null;
}

/** An item of the counts route's `counts`: the form's hex id exactly as the request sent it. */
export interface NetworkFormEntryCounts extends FormEntryCounts {
  formIdentifier: string;
}

export interface TimeSpentOnForm {
  averageEntryTime: number;
  minEntryTime: number;
  maxEntryTime: number;
}

interface NetworkReminders {
  type: string;
  recurrence: string;
  randomness: string | null;
  timezone: string | Timezone;
  ends: string | null;
  notification: ReminderNotification
}

interface FormStudy {
  identifier: string;
  scrambled: boolean;
}

export interface PostForm {
  title: string;
  studies: FormStudy[];
  description?: string;
  revisionDate: number;
  items: FormField[];
  anonymousResponsesAllowed: boolean;
  reminders?: Reminders;
  editExistingEntry?: boolean;
}

export interface PostFormResponse {
  identifier: string;
}

/** `POST form/:id/clone`'s answer: the copy, in the same study as its source. */
export interface CloneFormResponse {
  identifier: string;
}

export interface PostFormFile {
  filename: string;
  filetype: string;
  itemIdentifier?: string;
  size: number;
  withoutItemId: boolean;
}

export interface PresignedPostFields {
  key: string;
  bucket: string;
  'X-Amz-Algorithm': string;
  'X-Amz-Credential': string;
  'X-Amz-Date': string;
  Policy: string;
  'X-Amz-Signature': string;
}

export interface PresignedPost {
  url: string;
  fields: PresignedPostFields
}

export interface FormFileSignedResponse {
  presignedPost: PresignedPost;
  blob: NetworkBlob;
}

/**
 * One requirement a case has to meet before a form opens to it, named rather than encoded.
 *
 * The study states these as a condition tree over field and form *identifiers*
 * (`["==","@176b68","yes","#d03b269ece8acfa9"]`). Resolving those to a question and a form title
 * takes the study's form fields, which the pipeline's case query already holds and no client
 * response carries — so the API names them and this is what arrives.
 */
export interface FormRequirement {
  /** The title of the form the answer is expected on. */
  form: string;
  /**
   * The same form under the key the case list files its entries and enforcements by, so the card
   * can open it without matching on a title two forms are free to share. Absent from an API built
   * before the requirements became links, which leaves the row as text.
   */
  formIdentifier?: string;
  /**
   * The entry the condition read its answer from: the case's first on that form, which is the one
   * the API evaluates, so an answer given on any other entry of it would open nothing. Absent where
   * the case has not started the form, which is itself why the condition fails.
   */
  entryIdentifier?: string;
  /** The question's label, as the form asks it. */
  field: string;
  /** The comparison, as the raw operator — `==`, `!=`, `>`, `⊂=` and so on. The wording is ours. */
  operator: string;
  /** The answer it wants, already resolved to a choice's label where the field has choices. */
  value: string;
  /**
   * The field's own type (`Date`, `Choice`, `Number`, …), where the study still has the field.
   *
   * The operator alone does not say how to word a requirement. A study writes "the consent date is
   * filled in" as `> 0`, and "is more than 0" is not a sentence anybody can act on.
   */
  fieldType?: string;
}

/**
 * The requirements a case fails, with the shape of the study's condition kept.
 *
 * A flat list cannot say what `A && (B || C)` says, and the study this was written against says
 * exactly that: the liver was transplanted, *and* either the IRB waiver pair or the consent pair is
 * filled in. Flattened, the card listed five requirements under "all of these", three of which
 * nobody has to do.
 *
 * Already pruned by the time it arrives — a met branch is dropped, a group left with one child
 * arrives as that child — so every node here is something still outstanding.
 */
export type FormRequirementNode =
  /** Every child has to hold — the study's `&&`. */
  | { kind: 'all'; children: FormRequirementNode[] }
  /** Any one child opens the form — the study's `||`. */
  | { kind: 'any'; children: FormRequirementNode[] }
  /** One question and the answer it wants. */
  | ({ kind: 'requirement' } & FormRequirement);

/**
 * Why a form is closed to this case.
 *
 * `hidden` below is one boolean over two unrelated facts — a designer switched the form off for the
 * whole study, or this case's answers do not satisfy its `Show` condition — and until this arrived
 * the grid could only draw both as the same hatched cell saying "Not collected for this case".
 * `closeEntry` has no reason here on purpose: it does not close a pipeline cell, so giving it one
 * would change which cells are hatched rather than explain the ones that already are.
 */
export interface FormBlock {
  reason: 'hidden' | 'condition';
  /**
   * What the case still has to answer. Only set on `condition`, and absent there too where the
   * condition compares things the API cannot name — a leaf pointing at a deleted form or field, a
   * negated group — so nothing may assume it is present.
   */
  requirements?: FormRequirementNode;
}

export interface FormEnforcements {
  enforceOrder: boolean;
  hidden: boolean;
  onlyOnce: boolean;
  /**
   * The form's place in this study's form order. On a shared form it is this study's place, `null`
   * until this study first saves its form order; the other keys are still the owning study's. The
   * study's forms list comes back sorted by it, so no reader sorts by it again.
   */
  order: number | null;
  allowEntry: boolean;
  // When true, data entry for this form is closed for everyone (link, UI, SP team).
  closeEntry?: boolean;
  /**
   * Why `hidden` is true, where the API could say. Absent on a form nothing closes, and absent on
   * any response built before ENG-8665 — so every reader treats it as optional and falls back to
   * the unqualified wording.
   */
  blocked?: FormBlock;
  /**
   * The form links a study admin set on this form, as stored. Not a reminder: `reminders` is the
   * scheduled email and SMS. `PATCH study/:id/form-enforcements` stores whatever it is given, so
   * read this through `readFormLinks` rather than trusting its type.
   */
  links?: FormLink[];
}

/**
 * A message on one form's entry pointing the reader to another form of the same case. `type` is
 * there so a later kind of link can be stored beside this one; every reader skips a type it does
 * not know, so that link cannot break an older client.
 */
export interface FormLink {
  type: 'form';
  /** The target form, in hex. */
  formIdentifier: string;
  message: string;
}

/**
 * A form link as `GET form/:id` resolves it for one case. The API only sends the ones the case may
 * open, and only when the request names a case. It does not ask whether the caller may enter data
 * on the target, so `openableFormLinks` drops a new entry on a form the caller may not.
 */
export interface ResolvedFormLink extends FormLink {
  formTitle: string;
  /** The case's latest entry on the target form, or `null` to open a new one. */
  entryIdentifier: string | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isFormLink = (value: unknown): value is FormLink =>
  isRecord(value) &&
  value.type === 'form' &&
  typeof value.formIdentifier === 'string' &&
  value.formIdentifier !== '' &&
  typeof value.message === 'string';

const isResolvedFormLink = (value: unknown): value is ResolvedFormLink =>
  isFormLink(value) &&
  typeof (value as ResolvedFormLink).formTitle === 'string' &&
  ((value as ResolvedFormLink).entryIdentifier === null ||
    typeof (value as ResolvedFormLink).entryIdentifier === 'string');

/** The form links in `enforcements.links`, without any this client cannot read. */
export const readFormLinks = (links: unknown): FormLink[] =>
  Array.isArray(links) ? links.filter(isFormLink) : [];

/** The resolved links on a `GET form/:id` response, without any this client cannot read. */
export const readResolvedFormLinks = (links: unknown): ResolvedFormLink[] =>
  Array.isArray(links) ? links.filter(isResolvedFormLink) : [];

interface ReminderNotification {
  subject: string;
  body: string;
}

export interface Reminders {
  type: string;
  recurrence: string;
  randomness: string | null;
  timezone: Timezone;
  ends: string | null;
  notification: ReminderNotification
}

export interface FormTranslation {
  languageCode: string;
  language: string;
  title: string;
  description: string | null;
  items?: FormField[];
}

export interface IForm {
  identifier: string;
  createdAt: Date;
  updatedAt: Date;
  title: string;
  base64Id?: string;
  investigationIdentifier?: string;
  fieldCount: number;
  enforcements: FormEnforcements;
  anonymousResponsesAllowed?: boolean;
  editExistingEntry?: boolean;
  reminders?: Reminders;
  formTranslations?: FormTranslation[];

  sharedForm?: boolean;
  welcomeScreenTemplate?: string;
  archivedAt?: string;
  entrySaveButtonText?: string;
  showSaveEntryButtonAtBottom?: boolean;
  hasEntryPermission?: boolean;
  mayEnterData?: boolean;
  timeSpentData?: TimeSpentOnForm;

  multipleEntriesError?: string;
  cachePublicEntry?: boolean;
}

export class FormModel extends Model {
  identifier: string;
  createdAt: Date;
  updatedAt: Date;
  archivedAt?: Date;
  title: string;
  base64Id?: string;
  fieldCount: number;
  enforcements: FormEnforcements;
  studyId?: string;

  items?: FormField[];
  anonymousResponsesAllowed: boolean;
  editExistingEntry?: boolean;
  reminders?: Reminders;
  formTranslations?: FormTranslation[];

  sharedForm?: boolean;

  entries?: EntryModel[];
  welcomeScreenTemplate?: string;
  selectedCase?: CaseModel;
  entrySaveButtonText?: string;
  showSaveEntryButtonAtBottom?: boolean;
  hasEntryPermission?: boolean;
  /**
   * Whether the caller may enter data on the form, as the study's forms list says. A Manage forms
   * holder lists every form and may enter data on only some (users decision 23). Absent where the
   * read did not say, which counts as allowed.
   */
  mayEnterData?: boolean;
  timeSpentData?: TimeSpentOnForm;
  multipleEntriesError?: string;
  cachePublicEntry?: boolean;

  // Message shown when the backend reports the form's data entry is closed.
  closedMessage?: string;

  /** The form links this form's case may follow. Empty wherever the form was read without a case. */
  links: ResolvedFormLink[] = [];

  constructor({
    identifier,
    createdAt,
    updatedAt,
    title,
    base64Id,
    fieldCount,
    enforcements,
    anonymousResponsesAllowed,
    editExistingEntry,
    reminders,
    formTranslations,
    sharedForm,
    welcomeScreenTemplate,
    entrySaveButtonText,
    showSaveEntryButtonAtBottom,
    hasEntryPermission,
    mayEnterData,
    investigationIdentifier,
    archivedAt,
    timeSpentData,
    multipleEntriesError,
    cachePublicEntry
  }: IForm) {
    super();
    this.identifier = identifier;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.title = title;
    this.base64Id = base64Id;
    this.fieldCount = fieldCount;
    this.enforcements = enforcements;
    this.anonymousResponsesAllowed = anonymousResponsesAllowed == true;
    this.editExistingEntry = editExistingEntry;
    this.reminders = reminders;
    this.formTranslations = formTranslations;
    this.sharedForm = sharedForm;
    this.welcomeScreenTemplate = welcomeScreenTemplate;
    this.entrySaveButtonText = entrySaveButtonText;
    this.showSaveEntryButtonAtBottom = showSaveEntryButtonAtBottom;
    this.hasEntryPermission = hasEntryPermission;
    this.mayEnterData = mayEnterData;
    this.studyId = investigationIdentifier;
    this.archivedAt = archivedAt ? parseTimestamp(archivedAt).toJSDate() : undefined;
    this.timeSpentData = timeSpentData;
    this.multipleEntriesError = multipleEntriesError;
    this.cachePublicEntry = cachePublicEntry;
  }

  static mapFromBackend(form: NetworkForm) {
    if (form.reminders) {
      // One call for both shapes. `getTimezone` takes a name or an already-described `Timezone` —
      // and the branch that read `.value` threw on a reminder config that came back without a zone,
      // which fails every form in the study rather than one reminder.
      form.reminders.timezone = getTimezone(form.reminders.timezone);
    }

    const formModel = new FormModel({
      identifier: form.hexIdentifier ? form.hexIdentifier : form.identifier,
      createdAt: parseTimestamp(form.creation).toJSDate(),
      updatedAt: parseTimestamp(form.revisedOn).toJSDate(),
      title: form.title,
      base64Id: form.identifier,
      fieldCount: form.numFields,
      enforcements: form.enforcements ? form.enforcements : {
        enforceOrder: false,
        hidden: false,
        onlyOnce: false,
        order: 0,
        allowEntry: true
      },
      anonymousResponsesAllowed: form.anonymousResponsesAllowed,
      editExistingEntry: form.editExistingEntry,
      reminders: form.reminders as Reminders,
      formTranslations: form.translations,
      sharedForm: form.sharedForm,
      welcomeScreenTemplate: form.welcomeScreenTemplate,
      entrySaveButtonText: form.entrySaveButtonText,
      investigationIdentifier: form.investigationIdentifier,
      showSaveEntryButtonAtBottom: form.showSaveEntryButtonAtBottom,
      hasEntryPermission: form.hasEntryPermission,
      mayEnterData: form.edit,
      timeSpentData: form.timeSpentData,
      multipleEntriesError: form.multipleEntriesError,
      cachePublicEntry: form.cachePublicEntry
    });
    const items: FormField[] = [];
    if (form.items) {
      for (const item of form.items) {
        if (item.type === FormFieldType.CHOICE || item.type === FormFieldType.DROPDOWN) {
          if (typeof item.options.maximumChoicesCount === 'string') {
            item.options.maximumChoicesCount = parseInt(item.options.maximumChoicesCount);
          }
          item.options.maximumChoicesCount = item.options.maximumChoicesCount ? item.options.maximumChoicesCount : 2;
          item.options.allowOther = item.options.allowOther === true;

          if (item.options.choices) {
            let id = 1;
            item.options.choices.forEach(choice => {
              choice.id = id;
              id++;
            });
          }

          if (item.options.allowOther) {
            item.otherChoiceValue = {
              id: 99999999,
              label: 'Other',
              value: '-1',
              otherValue: ''
            };
          }
        }

        if (item.type === FormFieldType.FILE) {
          item.options = readFileLimits(item.options);
        }

        if (item.type === FormFieldType.MATRIX) {
          item.options.columns?.forEach(column => {
            if (column.type === MatrixColumnType.FILE) {
              column.options = readFileLimits(column.options ?? {});
            }
          });
        }

        if (item.options.requirement) {
          item.strictRequired = item.options.requirement === FIELD_REQUIREMENTS.STRICT_REQUIRED;
          item.nonStrictRequired = item.options.requirement === FIELD_REQUIREMENTS.NON_STRICT_REQUIRED;
          item.optional = item.options.requirement === FIELD_REQUIREMENTS.OPTIONAL;
          item.recommended = item.options.requirement === FIELD_REQUIREMENTS.RECOMMENDED;
        }

        if (item.type === FormFieldType.SECTION) {
          item.sectionExpanded = !item.options.collapsedInitially;

          item.options.fields?.forEach(field => {
            const foundField = form.items!.find(formItem => formItem.identifier == field);
            if (foundField != null) {
              foundField.sectionId = item.identifier;
            }
          });

          const sectionFields = item.options.fields;
          sectionFields?.forEach(fieldItem => {
            const foundField = form.items!.find(field => field.identifier === fieldItem);
            if (foundField) {
              foundField.expanded = item.sectionExpanded;
            }
          });
        }

        if (item.type === FormFieldType.UNIT && item.options.choices && item.options.choices.length > 0) {
          item.options.choices.push({
            id: 10,
            label: 'Clear',
            value: 'clear'
          });
        }

        if (isAssignEffectPresent(item)) {
          item.disable = true;
        }

        if (item.expanded != false) {
          item.expanded = true;
        }
        items.push(item);
      }
    }
    items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    formModel.items = items;
    formModel.closedMessage = form.closedMessage;
    formModel.links = readResolvedFormLinks(form.links);
    return formModel;
  }

  static mapFormWithEntries(form: NetworkForm) {
    const formModel = new FormModel({
      identifier: form.hexIdentifier ? form.hexIdentifier : form.identifier,
      createdAt: parseTimestamp(form.creation).toJSDate(),
      updatedAt: parseTimestamp(form.revisedOn).toJSDate(),
      title: form.title,
      base64Id: form.identifier,
      fieldCount: form.numFields,
      enforcements: form.enforcements ? form.enforcements : {
        enforceOrder: false,
        hidden: false,
        onlyOnce: false,
        order: 0,
        allowEntry: true
      }
    });

    form.entries?.forEach(entry => {
      // Typed as the model's `Date`, but this is the payload, so it is still the wire's ISO string.
      entry.createdAt = parseTimestamp(entry.createdAt as unknown as string).toJSDate();
    });

    formModel.entries = form.entries;

    return formModel;
  }
}

/**
 * One form in `PATCH study/:id/form-enforcements`. An own form sends its whole enforcements, which
 * the API writes over its row. A shared form sends only its place in this study's form order; the
 * API ignores every other key there, because they are the owning study's.
 */
export interface FormEnforcementsPatch {
  formIdentifier: string;
  enforcements: FormEnforcements | Pick<FormEnforcements, 'order'>;
}

export interface StudyFormEnforcementsPatch {
  forms: FormEnforcementsPatch[];
}

export interface FormFieldGraphData {
  yAxisMin?: number;
  yAxisMax?: number;
  yAxisStep?: number;
  xAxisMin?: number;
  xAxisMax?: number;
  xAxisStep?: number;
}

/**
 * A field on another form of the case that a field's Assign or Show reads, as spdata-api names it (ENG-9939). With a
 * case, the API rewrites that operand into the other entry's answer, so the conditions alone can't say which field it
 * was. The title and label are English, as sent, in a translated entry too.
 */
export interface CrossFormSource {
  effect: 'Assign' | 'Show';
  /** The form's 16-hex identifier, the `#` operand without its `#`. */
  formIdentifier: string;
  formTitle: string;
  /** The field, or for a `$VAR` the matrix holding the column. */
  fieldIdentifier: string;
  /** The field's label, or for a `$VAR` the column's. */
  fieldLabel: string;
  /** Sent only on a read for a case: whether an entry of that form answers the field. */
  answered?: boolean;
  /** A Show's only: the value its condition compares the field with. */
  answer?: string;
}

export interface FormField {
  id: string;
  identifier: string;
  type: FormFieldType;
  label: string;
  description: string;
  conditions?: object;
  /** The fields on other forms this field's logic reads. Absent, never empty, on a field that reads none. */
  crossFormSources?: CrossFormSource[];
  options: FormFieldOptions;
  formulaString: string;
  default: string;
  variableName?: string;

  icon?: CustomIcon;

  internalId?: string;
  expanded?: boolean;

  value?: string;
  numberValue?: number | null;
  choiceValue?: FormFieldChoice[] | FormFieldChoice;
  otherChoiceValue?: FormFieldChoice;
  dateValue?: Date;
  /** A datetime's zone. One saved before ENG-9047 may also hold the override mark, which `toWire` leaves out of it. */
  timezoneRecorded?: { timezone: string, overrideCalculatedValue?: boolean };
  /**
   * The reader overrode this Calculated field, so its Assign leaves it alone until they clear the answer. Saved as
   * `<id>_data` (`toWire`), and set and taken off by the field item context's `change()` (`withOverrideMark`).
   */
  overrideCalculatedValue?: boolean;
  locationValue?: GeolocationPosition;
  signatureValue?: SignatureData;
  fileIds?: string[];
  images?: {label: string, value: string}[];
  files?: BlobModel[];

  // surgeon dashboard summary fields
  /** Sent on a summary field whose `value` is a JSON list of timepoints rather than an answer. */
  hasTimePoints?: boolean;
  /** A timepoint summary field's timepoints, parsed out of `value` by `SurgeonDashboardData.mapFromBackend`. */
  timepointLabels?: string[];
  /** Sent on the Liver massage field when no timepoint answers yes: which of its two notes to draw. */
  massageNote?: 'notPerformed' | 'notCollected';

  // matrix field related
  matrixValues?: MatrixValue[];
  matrixTableRowValues?: MatrixTableRow[];
  hasExistingMatrixValue?: boolean;
  matrixDependencyResolved?: number;

  drillValue?: DrillData[] | string[];

  //effects
  show?: boolean;
  warn?: string;
  error?: string;
  valid?: string;
  disable?: boolean;
  strictRequired?: boolean;
  nonStrictRequired?: boolean;
  optional?: boolean;
  recommended?: boolean;

  showRequiredError?: boolean;

  /** When the field was archived, as the API sends it. The entry reads only whether it is set: an archived field is
   * kept in the list but never drawn, so it never blocks Save. */
  archivedAt?: string;

  /** Set by Save's check on a field whose answer breaks a rule set on it, and drawn under the field as its own message. */
  invalidInput?: MatrixRuleViolation;

  //import related
  changes?: FormFieldChanges;
  errors?: StudyChanges;

  sectionExpanded?: boolean;
  sectionId?: string;
  lastSectionField?: boolean;

  timeSeriesData?: TimeSeriesData[];
  historicalData?: TimeSeriesData[];
  centerHistoricData?: TimeSeriesData[];
  bicarbonateData?: TimeSeriesData[];
  glucoseData?: TimeSeriesData[];
  tpnData?: TimeSeriesData[];
  cumulativeTimeSeriesData?: TimeSeriesData[];


  //graph related
  filters?: DashboardCohortFilter[];
  liverCount?: number;
  centerLiverCount?: number;
  plotType?: string;
  order?: number;

  yAxisLabel?: string;
  perLiverKgOptionChecked?: boolean;

  loading?: boolean;
  showPerLiverKg?: boolean;

  graphData?: FormFieldGraphData;
  dataVersion?: number;
  graphVariable?: string;
  /** Set on a graph that draws each of `fields` as its own curve of this case's readings, with no cohort curves. */
  graphType?: 'liverCurves';
  /** The curves of a `liverCurves` graph, each a form field carrying its own `timeSeriesData`. */
  fields?: FormField[];
}

export type MedicationType = 'bolus' | 'infusion';

export interface TimeSeriesData {
  perfusionSelectedTimepoint?: string;
  perfusionActualTimepoint: number;
  medicationType?: MedicationType;
  value: number;
  ymin: number;
  ymax: number;
  ymin2?: number;
  ymax2?: number;
  /**
   * The dose given, on the intervention series (`glucoseData`, `tpnData`, `bicarbonateData`).
   * A dose marker plots at the reading it was given against, so `value` is not the dose and
   * this is what the tooltip reads instead.
   *
   * Declared rather than reached for through a `@ts-expect-error`, which is how it was read
   * before ENG-8153 — in four places, each one silently outside the type.
   */
  volume?: string | number;
}

export class FormFieldChoice {
  id: number;
  label: string;
  value: string | number;
  tooltip?: string;

  // for the multiple choice questions
  checked?: boolean;
  otherValue?: string;
  archived?: boolean;
  variableName?: string;

  constructor(choice: IFormFieldChoice) {
    this.id = choice.id;
    this.label = choice.label;
    this.value = choice.value;
    this.tooltip = choice.tooltip;
    this.checked = choice.checked;
    this.otherValue = choice.otherValue;
    this.archived = choice.archived;
    this.variableName = choice.variableName;
  }

  static createEmpty() {
    return new FormFieldChoice({
      id: 0,
      label: '',
      value: ''
    });
  }
}

export interface IFormFieldChoice {
  id: number;
  label: string;
  value: string | number;
  tooltip?: string;

  // for the multiple choice questions
  checked?: boolean;
  otherValue?: string;
  archived?: boolean;
  variableName?: string;
}

export interface FileConfig {
  enabled: boolean;
  value: string | number | null;
}

/**
 * A File field's limits, which a matrix File column carries under the same names. The API stores
 * each as a bare value, absent when unset; the builder edits each as a `FileConfig` toggle.
 */
export interface FileLimitOptions {
  allowedExtensions?: FileConfig | string;
  maximumSize?: FileConfig | number;
  maximumFileNumber?: FileConfig | number;
}

export const FILE_LIMIT_KEYS = ['maximumSize', 'allowedExtensions', 'maximumFileNumber'] as const;
export type FileLimitKey = (typeof FILE_LIMIT_KEYS)[number];

/** The builder's toggles for a File field or column with no limit set. */
export const noFileLimits = (): Required<FileLimitOptions> => ({
  maximumSize: { enabled: false, value: null },
  allowedExtensions: { enabled: false, value: '' },
  maximumFileNumber: { enabled: false, value: null }
});

/** The builder's options with a toggle for each limit, keeping any toggle already there. */
export const withFileLimitToggles = <T extends FileLimitOptions>(options: T): T => {
  const toggles: FileLimitOptions = noFileLimits();
  for (const key of FILE_LIMIT_KEYS) {
    if (options[key] != null) {
      delete toggles[key];
    }
  }
  return { ...options, ...toggles };
};

/** The stored options with each limit as the builder's toggle, switched on where a value is set. */
export const readFileLimits = <T extends FileLimitOptions>(options: T): T => {
  const limits: FileLimitOptions = noFileLimits();
  for (const key of FILE_LIMIT_KEYS) {
    if (options[key] != null) {
      limits[key] = { enabled: true, value: options[key] as string | number };
    }
  }
  return { ...options, ...limits };
};

/** The options with all three limits left out, for a column that is no longer a File column. */
export const withoutFileLimits = <T extends FileLimitOptions>(options: T): T => {
  const rest = { ...options };
  for (const key of FILE_LIMIT_KEYS) {
    delete rest[key];
  }
  return rest;
};

/** The options to store: each switched-on limit as its bare value, and a switched-off one left out. */
export const writeFileLimits = <T extends FileLimitOptions>(options: T): T => {
  const stored = withoutFileLimits(options);
  for (const key of FILE_LIMIT_KEYS) {
    const limit = options[key] as FileConfig | undefined;
    if (limit?.enabled) {
      Object.assign(stored, { [key]: limit.value });
    }
  }
  return stored;
};

export interface MatrixValue {
  rowId: string;
  values: MatrixColumnValue[];
  dynamicRows?: string[]
}

export interface MatrixColumnValue {
  id: string;
  value: string | number | boolean;
  timezoneRecorded?: { timezone: string } | string;
  conditions?: object;

  dateShifting?: boolean;
}

export interface MatrixRow {
  id: number;
  label: string;
  dynamicRowConfig?: DynamicRowConfig
  variableName?: string;
}

export interface DynamicRowConfig {
  copyRowData?: boolean;
  copyRowHeader?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  conditions?: any[];
  rowsAdded?: number;
}

export interface MatrixTableRow {
  rowId: string;
  rowHeader: string;
  dynamicRowConfig?: DynamicRowConfig;

  dynamicRows?: string[];
  enableClear?: boolean;
  columnValues?: MatrixColumn[]
}

interface DynamicRowColumnFormula {
  columnId: number;
  operator: string;
}

interface ColumnFormula {
  columnId: number;
  rowId: number;
  destColumnId: number;
  operator: string;
}

export interface MatrixColumn {
  id: number;
  label: string;
  type: MatrixColumnType;
  tintColor?: string;
  options?: MatrixColumnOptions;

  disable?: boolean;
  key?: string;
  dynamicRowColumnFormula?: DynamicRowColumnFormula
  columnFormula?: ColumnFormula

  textValue?: string;
  numberValue?: number | null;
  booleanValue?: boolean;
  dateValue?: Date;
  unShiftedDate?: Date;
  timezoneRecorded?: { timezone: string };
  choiceValue?: FormFieldChoice[] | FormFieldChoice;

  //table column related
  frozen?: boolean;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  default?: any[];

  tooltip?: string;

  variableName?: string;

  conditions?: object;

  //file realted
  fileIds?: string[];
}

interface UnitConfig {
  list: Unit[],
  default?: number,
  defaultFixed?: boolean
}

interface Unit {
  id: number;
  unit: string;
  description: string;
  conversions: UnitConversion[];
  fromFieldValue?: string;
}

export interface NetworkBlob {
  identifier: string;
  name: string;
  contentType: string;
  size: number;
  storedAt: string;
  hasThumbnail: boolean;
  thumbnailKey?: string;
  hasDisplayCopy?: boolean;
  conversionFailed?: boolean;
}

export interface NumberRange {
  rangeStart: number;
  rangeEnd: number;
}

interface FormFieldOptions extends FileLimitOptions {
  choices?: FormFieldChoice[];
  maximumChoicesCount?: number;
  allowOther?: boolean;
  mode?: DateMode | MatrixMode;
  absoluteDate?: boolean;
  dateShifting?: boolean;
  dateConstraints?: { min?: number | string; max?: number | string; };
  incrementSize?: number;
  startValue?: number;
  endValue?: number;
  startLabel?: string;
  endLabel?: string;
  numberOfSignatories?: string;
  allowEdit?: boolean;

  rows?: MatrixRow[];
  columns?: MatrixColumn[];
  /** Rules across a matrix row's cells, which Save checks (`matrixRules.ts`, ENG-9023). */
  rowRules?: MatrixRowRule[];

  //drilldown
  file?: string;
  drillLevels?: DrillLevel[];
  blob?: BlobModel;

  //new fields related
  units?: UnitConfig;
  format?: string;
  timeFormat?: number;

  requirement?: FIELD_REQUIREMENTS;

  fields?: string[];
  collapsedInitially?: boolean;

  overrideCalculation?: boolean;

  // Label tooltip
  tooltip?: string;


  range?: NumberRange
}

// eslint-disable-next-line no-shadow
export enum FIELD_REQUIREMENTS {
  STRICT_REQUIRED='STRICT_REQUIRED',
  NON_STRICT_REQUIRED='NON_STRICT_REQUIRED',
  OPTIONAL='OPTIONAL',
  RECOMMENDED='RECOMMENDED'
}

// eslint-disable-next-line no-shadow
export enum FormFieldType {
  NUMBER = 'Number',
  TEXT = 'Text',
  CHOICE = 'Choice',
  DROPDOWN = 'Dropdown',
  DATE = 'Date',
  LOCATION = 'Location',
  FILE = 'File',
  TITLE = 'Title',
  SLIDER = 'Slider',
  BARCODE = 'Barcode',
  SIGNATURE = 'Signature',
  MATRIX = 'Matrix',
  DRILLDOWN = 'Drilldown',

  // New fields
  UNIT='Unit',
  SECTION='Section',

  //Client only types
  BRANDING='Branding',
  SUBMIT_BUTTON='SubmitButton'
}

// eslint-disable-next-line no-shadow
export enum DateMode {
  DATE = 'date',
  TIME = 'time',
  DATE_TIME = 'datetime'
}

// eslint-disable-next-line no-shadow
export enum MatrixMode {
  RANK = 'rank',
}

export const FIELD_TYPES = [
  {
    id: 0,
    type: FormFieldType.NUMBER,
    label: 'Number',
    iconStyle: 'far',
    icon: 'hashtag'
  },
  {
    id: 1,
    type: FormFieldType.TEXT,
    label: 'Text',
    iconStyle: 'far',
    icon: 'text'
  },
  {
    id: 2,
    type: FormFieldType.CHOICE,
    label: 'Choice',
    iconStyle: 'far',
    icon: 'list-ul'
  },
  {
    id: 3,
    type: FormFieldType.DROPDOWN,
    label: 'Dropdown',
    iconStyle: 'far',
    icon: 'square-caret-down'
  },
  {
    id: 4,
    type: FormFieldType.DATE,
    label: 'Date',
    iconStyle: 'far',
    icon: 'calendar-days'
  },
  {
    id: 5,
    type: FormFieldType.LOCATION,
    label: 'GPS Location',
    iconStyle: 'far',
    icon: 'location-crosshairs'
  },
  {
    id: 6,
    type: FormFieldType.FILE,
    label: 'File',
    iconStyle: 'far',
    icon: 'file-arrow-up'
  },
  {
    id: 7,
    type: FormFieldType.TITLE,
    label: 'Title',
    iconStyle: 'far',
    icon: 'quote-left'
  },
  {
    id: 8,
    type: FormFieldType.SLIDER,
    label: 'Slider',
    iconStyle: 'far',
    icon: 'square-sliders'
  },
  {
    id: 9,
    type: FormFieldType.BARCODE,
    label: 'Barcode',
    iconStyle: 'far',
    icon: 'barcode-read'
  },
  {
    id: 10,
    type: FormFieldType.SIGNATURE,
    label: 'Signature',
    iconStyle: 'far',
    icon: 'file-signature'
  },
  {
    id: 11,
    type: FormFieldType.MATRIX,
    label: 'Matrix',
    iconStyle: 'fas',
    icon: 'table'
  },
  {
    id: 12,
    type: FormFieldType.DRILLDOWN,
    label: 'Drilldown',
    iconStyle: 'far',
    icon: 'list-tree'
  },
  {
    id: 13,
    type: FormFieldType.UNIT,
    label: 'Unit',
    iconStyle: 'far',
    icon: 'weight-hanging'
  },
  {
    id: 14,
    type: FormFieldType.SECTION,
    label: 'Section',
    iconStyle: 'fal',
    icon: 'file-half-dashed'
  }
];

export const createSampleFormPayload = (studyIdentifier: string): PostForm => {
  const username = LocalStorageUtils.getUserKeys().name?.split(' ')[0] || 'User';
  const title = `${username}'s ${getRandomAdjective()} form`;

  const studies = [{
    identifier: studyIdentifier,
    scrambled: true
  }];

  const item = createFormField();
  item.expanded = undefined;

  return {
    studies,
    title,
    revisionDate: new Date().getTime() / 1000,
    items: [item],
    description: '',
    anonymousResponsesAllowed: false
  };
};

export const createFormField = (): FormField => {
  return {
    id: uuid.v4(),
    identifier: generateHexIdentifier(6),
    type: FormFieldType.NUMBER,
    label: 'Example number field',
    description: '',
    conditions: [],
    options: {},
    formulaString: '',
    default: '',
    expanded: true
  };
};

export const getEmptyReminderConfig = () => ({
  type: 'rolling',
  recurrence: '0 10 * * *',
  randomness: null,
  timezone: getCurrentTimezone(),
  ends: null,
  notification: {
    subject: 'Time to complete a form',
    body: 'Don\'t forgot to log your data'
  }
});

// eslint-disable-next-line no-shadow
export enum MatrixColumnType {
  TEXT = 'Text',
  NUMBER = 'Number',
  SINGLE_CHOICE = 'RadioButton',
  MULTIPLE_CHOICE = 'Checkbox',
  DATE = 'Date',
  DROPDOWN = 'Dropdown',
  FILE = 'File',
}

export const MATRIX_COLUMN_TYPES = [
  {
    id: 0,
    type: MatrixColumnType.TEXT,
    label: 'Text',
    iconStyle: 'far',
    icon: 'text'
  },
  {
    id: 1,
    type: MatrixColumnType.NUMBER,
    label: 'Number',
    iconStyle: 'far',
    icon: 'hashtag'
  },
  {
    id: 2,
    type: MatrixColumnType.SINGLE_CHOICE,
    label: 'Single Choice',
    iconStyle: 'far',
    icon: 'dot-circle'
  },
  {
    id: 3,
    type: MatrixColumnType.MULTIPLE_CHOICE,
    label: 'Multiple choice',
    iconStyle: 'far',
    icon: 'check-square'
  },
  {
    id: 4,
    type: MatrixColumnType.DATE,
    label: 'Date',
    iconStyle: 'far',
    icon: 'calendar-alt'
  },
  {
    id: 5,
    type: MatrixColumnType.DROPDOWN,
    label: 'Dropdown',
    iconStyle: 'far',
    icon: 'square-caret-down'
  },
  {
    id: 6,
    type: MatrixColumnType.FILE,
    label: 'File',
    iconStyle: 'far',
    icon: 'file-arrow-up'
  }
];

interface MatrixColumnOptions extends FileLimitOptions {
  mode?: string;
  placeholder?: string;
  choices?: FormFieldChoice[];
  multipleChoice?: boolean;
  format?: string;
  timeFormat?: number;
  absoluteDate?: boolean;

  dateConstraints?: { min?: number | string; max?: number | string; };
  dateShifting?: boolean;

  showingShiftedDate?: boolean;

  range?: NumberRange;
}

// eslint-disable-next-line no-shadow
export enum FormFieldEffects {
  WARN='Warn',
  SHOW='Show',
  VALID='Valid',
  ASSIGN='Assign',
  DISABLE='Disable',
  REQUIRED='Required',
  DEFAULT='Default'
}

// Field type value interfaces

export interface GeolocationPosition {
  coords: Coordinates;
  timestamp: number;
}

export interface Coordinates {
  latitude: number;
  longitude: number;
  altitude?: number | null;
  accuracy: number;
  altitudeAccuracy?: number | null;
  heading?: number | null;
  speed?: number | null;
}

export interface PositionErrorCallback {
  (positionError: GeolocationPositionError): void;
}

interface GeolocationPositionError {
  code: number;
  message: string;
  PERMISSION_DENIED: number;
  POSITION_UNAVAILABLE: number;
  TIMEOUT: number;
}

export interface Signatory {
  signature: string;
  signatory: string;
  email: string;
  dateTime: number;
  emailSent?: boolean;
  revoked?: boolean;
  removed?: boolean;
}

export interface SignatureData {
  signatories: Signatory[];
}

export interface NetworkLocationData {
  latitude: number;
  longitude: number;
  accuracy: number,
  timestamp: number;
}

// eslint-disable-next-line no-shadow
export enum DELETE_CONFIRMATION_TYPE {
  FORM='form',
  ARCHIVE='Archive'
}
